import { z } from 'zod';

/**
 * Open set, deliberately. The ops plane and the tenant self-serve area each
 * add capabilities the shell has never heard of, and a bundle built before they
 * landed must keep working against an API that sends them. `z.enum([...])`
 * here would turn every new capability into a parse failure that logs the
 * whole caller out of their own application.
 */
export const capabilitySchema = z.string();

/**
 * One row of the caller's membership list. `campaign_id` and `campaign_name`
 * are null for a tenant-wide row -- a tenant admin who holds the grant across
 * every campaign rather than in one of them.
 *
 * `role` is a plain string, carried for display only. Nothing in the client
 * branches on it, and -- today -- nothing displays it either, which is why it
 * is optional rather than required: see the note on `used_mfa` below.
 */
export const membershipSchema = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  campaign_id: z.string().nullable(),
  campaign_name: z.string().nullable(),
  role: z.string().optional(),
});

/** `api`'s `CampaignEntry.state` (`socialsrc_core.db.enums.CampaignState`):
 *  additive-closed, unlike `capabilitySchema` -- the switcher's own row
 *  renders every member, `ARCHIVED` included, since `/control/me` does not
 *  filter archived campaigns out of the listing (only out of
 *  `campaign_count`). */
export const campaignStateSchema = z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']);
export type CampaignStateValue = z.infer<typeof campaignStateSchema>;

/** One campaign the caller can reach, with the capabilities the campaign
 *  itself carries (platform design §17.8): a tenant-wide role plus any row
 *  naming this campaign, not the pooled set.
 *
 *  `state`, `member_count` and `reach_7d` are the switcher row's own
 *  comparison figures (`owner-decisions-align-57-2-04`). `.optional()`
 *  (`reach_7d` also `.nullable()`, matching `CampaignEntry.reach_7d: int |
 *  None`) so every fixture built before these fields existed keeps parsing;
 *  a consumer checks `!== undefined` before rendering the row's figures. */
export const campaignEntrySchema = z.object({
  campaign_id: z.string(),
  campaign_name: z.string(),
  capabilities: z.array(capabilitySchema),
  state: campaignStateSchema.optional(),
  member_count: z.number().optional(),
  reach_7d: z.number().nullable().optional(),
});

/** One organisation the caller holds a membership in, with its own
 *  capabilities and the campaigns inside it the caller can read (platform
 *  design §17.8). `capabilities` here is per organisation, never the pooled
 *  set `capabilities` on `Me` is.
 *
 *  `balance_credits`, `consumed_credits_7d` and `runway_until` are the
 *  switcher's own comparison figures (`owner-decisions-align-57-2-03`),
 *  named for `api`'s `OrganisationEntry` fields exactly -- Zod drops an
 *  unrecognised key silently, so a client-invented name here parses to
 *  `undefined` against the real wire and never renders. One
 *  per-organisation aggregation, not a `/tenants/{t}/credits` call per row.
 *  `.optional()` beside `.nullable()` so every fixture built before this
 *  field existed keeps parsing unchanged; a consumer guards both the same
 *  way (`== null`), never `=== null` alone. `null` on either
 *  `balance_credits` or `consumed_credits_7d` is the server's own courtesy
 *  withholding (a caller who cannot see this organisation's credits, or one
 *  not yet aggregated), and the client renders neither rather than one
 *  figure with no sibling to compare it against. `runway_until` -- a
 *  deadline, not a week count, matching the producer's own `datetime | None`
 *  -- stays independently nullable: it has no projection while
 *  `consumed_credits_7d` is zero. */
export const organisationEntrySchema = z.object({
  tenant_id: z.string(),
  tenant_name: z.string(),
  capabilities: z.array(capabilitySchema),
  tenant_wide_read: z.boolean(),
  balance_credits: z.number().nullable().optional(),
  consumed_credits_7d: z.number().nullable().optional(),
  runway_until: z.string().nullable().optional(),
  campaigns: z.array(campaignEntrySchema),
});

export type CampaignEntry = z.infer<typeof campaignEntrySchema>;
export type OrganisationEntry = z.infer<typeof organisationEntrySchema>;

/**
 * `used_mfa` and `membership.role` are optional, and deliberately so.
 *
 * Both are declared by the API contract and neither is read anywhere in this
 * client. A required field nothing reads is a field that can only ever cost
 * something: if the endpoint renames or drops it, `meSchema` fails, `useMe`
 * errors, and because `AppShell` short-circuits on `unauthenticated` alone the
 * caller gets the full chrome with an empty campaign switcher, an empty nav
 * and "your access rights could not be confirmed" on every gated route -- a
 * working-looking, entirely inert application, which is a worse failure than a
 * hard error and was one field away.
 *
 * They stay in the schema rather than being deleted so the first consumer gets
 * them typed instead of re-deriving the contract; a consumer that starts
 * reading one makes it required in the same commit.
 *
 * `firebase_uid`, `email`, `memberships`, `capabilities` and `organisations`
 * stay required: the shell reads all five, so a drift in any of them has to
 * be loud. The campaign routes resolve a campaign's organisation from
 * `organisations` (`../control/routes.ts`, `./orgContext.ts`), so a missing
 * or malformed listing has to fail the whole session rather than blank one
 * page.
 */
export const meSchema = z.object({
  firebase_uid: z.string(),
  email: z.string().nullable(),
  used_mfa: z.boolean().optional(),
  memberships: z.array(membershipSchema),
  capabilities: z.array(capabilitySchema),
  organisations: z.array(organisationEntrySchema),
});

export type Membership = z.infer<typeof membershipSchema>;
export type Me = z.infer<typeof meSchema>;
